import { RiskRating as RiskRatingEnum } from "~/graphql/supergraphEnums";
import { type RiskRating } from "~/graphql/supergraphTypes";
import { RISK_VALUES } from "~/pages/Compliance/riskRating/constants";

export type RiskRatingType = {
  __typename?: "RiskRatingType" | undefined;
  id?: number;
  calculatedRating?: RiskRating | null;
  overriddenRating?: RiskRating | null;
};

export type RiskRatingOrNull = RiskRating | "";

/**
 * Gets the effective risk rating by preferring overriddenRating over calculatedRating
 * @param riskRating - The risk rating object
 * @returns The effective risk rating or null
 */
export const getEffectiveRiskRating = (riskRating?: RiskRatingType | null): RiskRating => {
  return riskRating?.overriddenRating ?? riskRating?.calculatedRating ?? RiskRatingEnum.None;
};

/**
 * Calculates the position risk rating by taking the highest of three risk ratings:
 * 1. Investor Risk Rating
 * 2. Beneficiary Risk Rating (payment account profile)
 * 3. Payment Account Risk Rating
 *
 * @param investorRiskRating - The investor's risk rating
 * @param beneficiaryRiskRating - The payment account profile's risk rating
 * @param paymentAccountRiskRating - The payment account's risk rating
 * @returns The highest risk rating among the three, or null if all are null
 */
export const getPositionRiskRating = (
  investorRiskRating: RiskRatingType,
  beneficiaryRiskRating?: RiskRatingType | null,
  paymentAccountRiskRating?: RiskRatingType | null,
): RiskRatingOrNull => {
  const ratings = [
    getEffectiveRiskRating(investorRiskRating),
    getEffectiveRiskRating(beneficiaryRiskRating),
    getEffectiveRiskRating(paymentAccountRiskRating),
  ];

  if (ratings.length === 0) return "";

  return ratings.reduce((highest, current) =>
    RISK_VALUES[current] > RISK_VALUES[highest] ? current : highest,
  );
};
